// Optional browser services of the tenant (config/browser_services.php,
// shared server-side in the bootstrap context, see getTenantContext()).
// The minimal Sentry profile backs a "technical tools only" cookie notice:
// keep it in sync with the notice before relaxing anything here.

export const SENTRY_DSN = 'https://0926d6d5753a4591c42bf4463b9ec52f@o4509411733929989.ingest.de.sentry.io/4509412029235281';

const STANDARD = 'standard';
const MINIMAL = 'minimal';

// Integrations that collect navigation, requests, sessions or performance data.
const MINIMAL_EXCLUDED_INTEGRATIONS = new Set([
    'Breadcrumbs',
    'BrowserSession',
    'HttpContext',
    'BrowserTracing',
    'Replay',
    'ReplayCanvas',
    'Feedback',
]);

const MAX_EXCEPTIONS = 5;
const MAX_FRAMES = 60;
const MAX_TYPE_LENGTH = 120;
const MAX_FUNCTION_LENGTH = 200;
const SAFE_IDENTIFIER = /^[\w$.<>\[\]\- :/@]*$/;

// Unknown or missing profiles fail closed: no Clarity, minimal Sentry, no
// tutorial dates. Stored tutorial dates are removed only on an explicit false,
// so a page without profile never deletes another tenant's data.
export function resolveBrowserServices(raw) {
    const profile = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};

    return {
        clarity: profile.clarity === true,
        sentry: profile.sentry === STANDARD ? STANDARD : MINIMAL,
        tutorials: profile.tutorials === true,
        forgetTutorials: profile.tutorials === false,
    };
}

// Tutorial visit dates follow the profile: kept, skipped or removed.
export function syncTutorialSession({ profile, userId, tutorialManager, forgetStoredTutorialSessions }) {
    const services = resolveBrowserServices(profile);
    if (services.tutorials) return tutorialManager.initUserTutorialSession(userId ?? null);
    tutorialManager.initUserTutorialSession(null);
    if (services.forgetTutorials) forgetStoredTutorialSessions();
    return null;
}

const boundedIdentifier = (value, max) => {
    if (typeof value !== 'string' || value.length === 0) return undefined;
    const trimmed = value.slice(0, max);
    return SAFE_IDENTIFIER.test(trimmed) ? trimmed : undefined;
};

// Script locations keep origin and path (needed by the source maps), never
// query strings, fragments or credentials.
export function cleanFrameFilename(filename) {
    if (typeof filename !== 'string' || filename.length === 0) return undefined;
    if (/^(<anonymous>|native|\[native code\])$/.test(filename)) return filename;
    try {
        const url = new URL(filename);
        if (!['http:', 'https:', 'app:'].includes(url.protocol)) return undefined;
        return `${url.protocol}//${url.host}${url.pathname}`;
    } catch {
        return undefined;
    }
}

const cleanFrame = (frame) => {
    const cleaned = {};
    const filename = cleanFrameFilename(frame?.filename ?? frame?.abs_path);
    if (filename) {
        cleaned.filename = filename;
        cleaned.abs_path = filename;
    }
    const fn = boundedIdentifier(frame?.function, MAX_FUNCTION_LENGTH);
    if (fn) cleaned.function = fn;
    for (const key of ['lineno', 'colno']) {
        if (Number.isInteger(frame?.[key])) cleaned[key] = frame[key];
    }
    if (typeof frame?.in_app === 'boolean') cleaned.in_app = frame.in_app;
    return cleaned;
};

const cleanException = (exception) => {
    const cleaned = { type: boundedIdentifier(exception?.type, MAX_TYPE_LENGTH) ?? 'Error' };
    const mechanism = exception?.mechanism;
    if (mechanism && typeof mechanism === 'object') {
        cleaned.mechanism = {
            type: boundedIdentifier(mechanism.type, MAX_TYPE_LENGTH) ?? 'generic',
            ...(typeof mechanism.handled === 'boolean' ? { handled: mechanism.handled } : {}),
        };
    }
    const frames = Array.isArray(exception?.stacktrace?.frames) ? exception.stacktrace.frames : [];
    if (frames.length) {
        // Sentry orders frames oldest first: keep the frames closest to the error.
        cleaned.stacktrace = { frames: frames.slice(-MAX_FRAMES).map(cleanFrame) };
    }
    return cleaned;
};

// Final allow-list for the minimal profile: error type, cleaned stack,
// release and environment. Messages, URLs, user, request, tags, extra,
// contexts and breadcrumbs never leave the browser.
export function scrubMinimalEvent(event) {
    if (!event || typeof event !== 'object') return null;
    const values = Array.isArray(event.exception?.values) ? event.exception.values : [];
    if (!values.length) return null;

    const cleaned = {
        exception: { values: values.slice(-MAX_EXCEPTIONS).map(cleanException) },
        level: event.level === 'fatal' ? 'fatal' : 'error',
    };
    for (const key of ['event_id', 'timestamp', 'platform', 'release', 'environment', 'sdk']) {
        if (event[key] !== undefined) cleaned[key] = event[key];
    }
    return cleaned;
}

export function minimalSentryOptions() {
    return {
        sendDefaultPii: false,
        attachProps: false,
        maxBreadcrumbs: 0,
        sendClientReports: false,
        integrations: (defaults) => defaults.filter((integration) => !MINIMAL_EXCLUDED_INTEGRATIONS.has(integration?.name)),
        beforeBreadcrumb: () => null,
        beforeSend: (event) => scrubMinimalEvent(event),
        beforeSendTransaction: () => null,
        beforeSendSpan: () => null,
    };
}

const syncClarityContext = (Clarity, tenantContext, pageComponent) => {
    const { name, user } = tenantContext;
    const userId = user?.id;

    if (userId !== undefined && userId !== null) {
        Clarity.identify(String(userId), undefined, undefined, user?.name);
    }
    if (name) Clarity.setTag('tenant', name);
    if (pageComponent) Clarity.setTag('page', pageComponent);
};

function startStandardSentry({ app, Sentry, tenantContext, initialPage }) {
    Sentry.init({
        app,
        dsn: SENTRY_DSN,
        sendDefaultPii: true,
        tracesSampleRate: 1.0,
    });

    const { name, user } = tenantContext;
    Sentry.setUser({
        id: user?.id,
        email: user?.email,
        name: user?.name,
        tenant: name,
    });
    Sentry.setTag('inertia_page', initialPage?.component);
    Sentry.setTag('url', window.location.pathname);
}

function startClarity({ Clarity, projectId, router, tenantContext, initialPage, getTenantContext }) {
    try {
        Clarity.init(projectId);
        syncClarityContext(Clarity, tenantContext, initialPage?.component);

        // Re-identify and update tags on each SPA navigation.
        router.on('navigate', (event) => {
            try {
                syncClarityContext(Clarity, getTenantContext(), event.detail.page.component);
            } catch {
                // silently ignore navigation tracking errors
            }
        });
    } catch (error) {
        console.warn('Clarity initialization failed', error);
    }
}

// Starts the services allowed by the tenant profile. Production only.
export function startBrowserServices({
    app,
    Sentry,
    Clarity,
    router,
    initialPage,
    getTenantContext,
    profile,
    production,
    clarityProjectId,
}) {
    const services = resolveBrowserServices(profile);
    const started = { sentry: null, clarity: false };
    if (!production) return started;

    const tenantContext = getTenantContext();
    if (services.sentry === STANDARD) {
        startStandardSentry({ app, Sentry, tenantContext, initialPage });
    } else {
        Sentry.init({ app, dsn: SENTRY_DSN, ...minimalSentryOptions() });
    }
    started.sentry = services.sentry;

    if (services.clarity && clarityProjectId) {
        startClarity({ Clarity, projectId: clarityProjectId, router, tenantContext, initialPage, getTenantContext });
        started.clarity = true;
    }

    return started;
}
